/**
 * Review System rating screen (Jonathan 2026-05-21).
 *
 * Shown after a Variant-B session completes, before the EndScene summary.
 * The participant rates the level they just played: Fun (1–5 stars) and
 * Difficulty (1–5 skulls). The rating is persisted (`RatingService`) and
 * logged, then control passes to the EndScene summary.
 *
 * Controller-first: ↑/↓ move between the Fun row, Difficulty row, and the
 * Submit button; ←/→ set the focused row's value; A / Start submits; Esc
 * skips (still advances to the summary, logged as skipped).
 */

import Phaser from "phaser";

import { NATIVE_HEIGHT, NATIVE_WIDTH, PALETTE, UGC_CYAN } from "../config";
import { t } from "../i18n";
import type { LevelRating, SessionSummary } from "../types";

import { AuthorIdentityService } from "../services/AuthorIdentityService";
import { InputService } from "../services/InputService";
import { RatingService, levelRatingKey } from "../services/RatingService";

export interface RatingSceneInit {
  summary: SessionSummary;
  /** Scene key of the runner to stop on hand-off to the EndScene. The rating
   *  popup is launched OVER the (paused) runner so the game stays visible
   *  around the frame, so the runner must be stopped explicitly afterwards. */
  runnerKey?: string;
}

const STAR_COUNT = 5;
const ROW_FUN = 0;
const ROW_DIFFICULTY = 1;
const ROW_SUBMIT = 2;

// Centred popup panel, inset from the screen edges so the (paused) runner
// stays visible around the frame.
const PANEL_X = 40;
const PANEL_Y = 20;
const PANEL_W = 304;
const PANEL_H = 176;

export class RatingScene extends Phaser.Scene {
  private input2!: InputService;
  private telemetry!: {
    emit: (l: 1 | 2 | 3, t: string, d?: Record<string, number | string | boolean>) => void;
  };
  private summary!: SessionSummary;
  private authorSeed = 0;
  private runnerKey = "runner";

  private rating: LevelRating = { fun: 3, difficulty: 3 };
  private funGlyphs: Phaser.GameObjects.Text[] = [];
  private diffGlyphs: Phaser.GameObjects.Text[] = [];
  private rowMarkers: Phaser.GameObjects.Text[] = [];
  private submitText!: Phaser.GameObjects.Text;
  /** Framed cursor that surrounds the focused control (so focus is obvious
   *  beyond colour alone). */
  private focusBox!: Phaser.GameObjects.Rectangle;

  private focusRow = ROW_FUN;
  private prevMoveX = 0;
  private prevMoveY = 0;
  private done = false;

  constructor() {
    super({ key: "RatingScene" });
  }

  init(data: RatingSceneInit): void {
    this.summary = data.summary;
    this.authorSeed = data.summary.levelAuthorSeed ?? 0;
    this.runnerKey = data.runnerKey ?? "runner";
  }

  create(): void {
    this.input2 = new InputService(this);
    this.telemetry =
      (this.registry.get("telemetryService") as RatingScene["telemetry"] | undefined) ??
      { emit: () => undefined };

    // Pre-fill any prior rating the player gave this level.
    const prior = RatingService.getGiven(levelRatingKey(this.authorSeed));
    if (prior) this.rating = { ...prior };

    // Transparent camera (no setBackgroundColor) + a half-dim overlay so the
    // paused runner stays visible around the popup; an opaque framed panel
    // holds the rating UI. Dim/panel sit at negative depth so the rest of the
    // UI (default depth 0) renders above them without per-object depth tweaks.
    const cx = NATIVE_WIDTH / 2;
    this.add
      .rectangle(0, 0, NATIVE_WIDTH, NATIVE_HEIGHT, 0x000000, 0.45)
      .setOrigin(0, 0)
      .setDepth(-2);
    this.add
      // Dark, fully-opaque panel so the light/white rating text reads clearly
      // (the parchment fill had poor contrast against white).
      .rectangle(PANEL_X, PANEL_Y, PANEL_W, PANEL_H, PALETTE.hudPanel, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, UGC_CYAN)
      .setDepth(-1);
    this.add.rectangle(PANEL_X + 3, PANEL_Y + 3, 4, 4, UGC_CYAN, 1).setOrigin(0, 0);

    this.add
      .text(cx, PANEL_Y + 14, t("rating.title"), {
        fontFamily: "monospace",
        fontSize: "12px",
        color: this.hex(PALETTE.hudText),
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5);

    // Author identity (avatar + name) of the level being rated.
    const identityService =
      (this.registry.get("authorIdentityService") as AuthorIdentityService | undefined) ??
      new AuthorIdentityService();
    const identity = identityService.forSeed(this.authorSeed);
    identityService.drawAvatar(this, cx - 16, 44, identity, 2).setDepth(2);
    const authorName = this.summary.levelAuthorName ?? identity.username;
    this.add
      .text(cx, 86, t("rating.by", { author: `@${authorName}` }), {
        fontFamily: "monospace",
        fontSize: "9px",
        color: this.hex(PALETTE.geo),
      })
      .setOrigin(0.5, 0.5);

    // Fun row (stars) + Difficulty row (skulls).
    this.buildRow(ROW_FUN, 112, t("rating.fun"), this.funGlyphs);
    this.buildRow(ROW_DIFFICULTY, 134, t("rating.difficulty"), this.diffGlyphs);

    // Submit button.
    this.submitText = this.add
      .text(cx, 160, t("rating.submit"), {
        fontFamily: "monospace",
        fontSize: "11px",
        color: this.hex(PALETTE.hudText),
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5)
      .setInteractive({ useHandCursor: true });
    this.submitText.on("pointerdown", () => this.submit());

    // Focus cursor — a framed box around the focused row / button.
    this.focusBox = this.add
      .rectangle(0, 0, 10, 10, UGC_CYAN, 0.12)
      .setOrigin(0.5, 0.5)
      .setStrokeStyle(2, UGC_CYAN)
      .setDepth(5);

    this.add
      .text(cx, PANEL_Y + PANEL_H - 18, t("rating.hint"), {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.hex(PALETTE.hudDim),
      })
      .setOrigin(0.5, 0.5);
    this.add
      .text(cx, PANEL_Y + PANEL_H - 9, t("rating.skip"), {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.hex(PALETTE.hudDim),
      })
      .setOrigin(0.5, 0.5);

    this.repaint();
    this.telemetry.emit(1, "ugc.level.rating.open", { authorSeed: this.authorSeed });
  }

  override update(): void {
    if (this.done) return;
    const s = this.input2.read();

    if (s.escPressed) {
      this.skip();
      return;
    }

    const dy = s.moveY > 0.5 ? 1 : s.moveY < -0.5 ? -1 : 0;
    if (dy !== this.prevMoveY) {
      this.prevMoveY = dy;
      if (dy !== 0) {
        this.focusRow = Phaser.Math.Clamp(this.focusRow + dy, ROW_FUN, ROW_SUBMIT);
        this.repaint();
      }
    }

    const dx = s.moveX > 0.5 ? 1 : s.moveX < -0.5 ? -1 : 0;
    if (dx !== this.prevMoveX) {
      this.prevMoveX = dx;
      if (dx !== 0) this.adjustValue(dx);
    }

    if (s.interactPressed || s.attackPressed || s.jumpPressed || s.startPressed) {
      if (this.focusRow === ROW_SUBMIT) {
        this.submit();
      } else {
        this.focusRow = ROW_SUBMIT;
        this.repaint();
      }
    }
  }

  // -- layout / paint -------------------------------------------------------

  private buildRow(row: number, y: number, label: string, glyphs: Phaser.GameObjects.Text[]): void {
    const marker = this.add
      .text(58, y, "▶", { fontFamily: "monospace", fontSize: "9px", color: this.hex(UGC_CYAN) })
      .setOrigin(0.5, 0.5);
    this.rowMarkers[row] = marker;
    this.add
      .text(72, y, label, {
        fontFamily: "monospace",
        fontSize: "9px",
        color: this.hex(PALETTE.hudText),
      })
      .setOrigin(0, 0.5);
    const startX = 168;
    for (let i = 0; i < STAR_COUNT; i++) {
      const g = this.add
        .text(startX + i * 22, y, "", {
          fontFamily: "monospace",
          fontSize: "14px",
        })
        .setOrigin(0.5, 0.5)
        .setInteractive({ useHandCursor: true });
      const value = i + 1;
      g.on("pointerdown", () => {
        if (row === ROW_FUN) this.rating.fun = value;
        else this.rating.difficulty = value;
        this.focusRow = row;
        this.repaint();
      });
      glyphs.push(g);
    }
  }

  private adjustValue(delta: number): void {
    if (this.focusRow === ROW_FUN) {
      this.rating.fun = Phaser.Math.Clamp(this.rating.fun + delta, 1, STAR_COUNT);
    } else if (this.focusRow === ROW_DIFFICULTY) {
      this.rating.difficulty = Phaser.Math.Clamp(this.rating.difficulty + delta, 1, STAR_COUNT);
    }
    this.repaint();
  }

  private repaint(): void {
    this.funGlyphs.forEach((g, i) => {
      const filled = i < this.rating.fun;
      g.setText(filled ? "★" : "☆");
      g.setColor(this.hex(filled ? PALETTE.geo : PALETTE.hudDim));
    });
    this.diffGlyphs.forEach((g, i) => {
      const filled = i < this.rating.difficulty;
      g.setText("☠");
      g.setColor(this.hex(filled ? PALETTE.hp : PALETTE.hudDim));
    });
    this.rowMarkers.forEach((m, i) => m?.setVisible(i === this.focusRow));
    if (this.submitText) {
      this.submitText.setColor(this.hex(this.focusRow === ROW_SUBMIT ? PALETTE.geo : PALETTE.hudText));
    }
    if (this.focusBox) {
      if (this.focusRow === ROW_SUBMIT) {
        this.focusBox.setPosition(NATIVE_WIDTH / 2, 160).setSize(110, 16);
      } else {
        this.focusBox.setPosition(158, this.focusRow === ROW_FUN ? 112 : 134).setSize(224, 16);
      }
    }
  }

  // -- finish ---------------------------------------------------------------

  private submit(): void {
    if (this.done) return;
    this.done = true;
    RatingService.submit(levelRatingKey(this.authorSeed), this.rating);
    this.telemetry.emit(2, "ugc.level.rating.submit", {
      authorSeed: this.authorSeed,
      fun: this.rating.fun,
      difficulty: this.rating.difficulty,
    });
    this.flashThanksAndAdvance();
  }

  private skip(): void {
    if (this.done) return;
    this.done = true;
    this.telemetry.emit(1, "ugc.level.rating.skip", { authorSeed: this.authorSeed });
    this.goToEnd();
  }

  private flashThanksAndAdvance(): void {
    const cx = NATIVE_WIDTH / 2;
    this.add
      .text(cx, 160, t("rating.thanks"), {
        fontFamily: "monospace",
        fontSize: "11px",
        color: this.hex(PALETTE.geo),
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5)
      .setDepth(10);
    this.submitText.setVisible(false);
    this.time.delayedCall(650, () => this.goToEnd());
  }

  /** Stop the paused runner (this scene was launched over it) and hand off to
   *  the EndScene summary. */
  private goToEnd(): void {
    this.scene.stop(this.runnerKey);
    this.scene.start("end", this.summary);
  }

  private hex(n: number): string {
    return `#${n.toString(16).padStart(6, "0")}`;
  }
}
